package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.remove
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.InputSize
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/*
 * Removal dialog, built on Silk `Overlay` (the native dialog element and its modal API are gone): the
 * card is centered over the dimmed fullscreen overlay, a full-size sibling backdrop handles
 * click-to-cancel, and ESC is handled by a window-level listener. Strings/behavior match the old
 * dialog 1:1.
 */

val RemovalDialogStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        .maxWidth(26.25.cssRem)
}

val RemovalDialogTitleStyle = CssStyle.base {
    Modifier.margin(0.cssRem)
}

val RemovalDialogActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.FlexEnd)
        .gap(0.5.cssRem)
        .margin(top = 1.cssRem)
}

val RemovalDialogNoteStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .margin(top = 0.5.cssRem)
}

@Composable
internal fun RemovalDialog(state: DashboardState, launchAction: ((suspend () -> Unit) -> Unit)) {
    state.pendingRemoval?.let { person ->
        // Escape hatch: Compose HTML has no key-event composable; a window keydown listener (removed on
        // dispose) implements ESC-to-close.
        DisposableEffect(person.idpessoa) {
            val listener = EventListener { event ->
                if ((event as? KeyboardEvent)?.key == "Escape") state.pendingRemoval = null
            }
            window.addEventListener("keydown", listener)
            onDispose { window.removeEventListener("keydown", listener) }
        }

        Overlay(contentAlignment = Alignment.Center) {
            // Full-size sibling backdrop: clicks outside the card cancel; clicks on the card never reach it.
            // The dim itself is `Overlay`'s own background (`palette.overlay`, ~10% black).
            Box(Modifier.fillMaxSize().onClick { state.pendingRemoval = null })

            Column(modifier = RemovalDialogStyle.toModifier().then(Modifier.id("confirm-removal"))) {
                H3(attrs = RemovalDialogTitleStyle.toAttrs()) { Text("Remover do painel") }
                P { Text("Confirmar que ${person.nome} não está mais no prédio?") }
                Label(attrs = { attr("for", "removal-note") }) { Text("Observação (opcional)") }
                TextInput(
                    text = state.removalNote,
                    onTextChange = { state.removalNote = it },
                    modifier = RemovalDialogNoteStyle.toModifier().then(Modifier.id("removal-note")),
                    placeholder = "ex.: saiu sem registrar",
                    size = InputSize.SM,
                    ref = ref {
                        // Escape hatch: Silk's `TextInput` exposes no `maxlength`/autofocus, so set them on mount.
                        it.maxLength = 200
                        it.focus()
                    },
                )
                Div(attrs = RemovalDialogActionsStyle.toAttrs()) {
                    Button(
                        onClick = { state.pendingRemoval = null },
                        variant = DefaultButtonVariant,
                        size = ButtonSize.SM,
                    ) {
                        Text("Cancelar")
                    }
                    Button(
                        onClick = {
                            val toRemove = state.pendingRemoval
                            state.pendingRemoval = null
                            if (toRemove != null) launchAction { state.remove(toRemove) }
                        },
                        variant = DangerButtonVariant,
                        size = ButtonSize.SM,
                    ) {
                        Text("Remover")
                    }
                }
            }
        }
    }
}
